import './index.scss';

import {
  AlignCenterOutlined,
  AuditOutlined,
  BankOutlined,
  CommentOutlined,
  DatabaseOutlined,
  DollarOutlined,
  EditOutlined,
  ExportOutlined,
  FileOutlined,
  FormOutlined,
  ImportOutlined,
  LinkOutlined,
  MergeCellsOutlined,
  ProfileOutlined,
  ProjectOutlined,
  QuestionCircleOutlined,
  SolutionOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
  RadarChartOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Layout, Menu } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router';
import logotype from '../../assets/icons/ai-admin-logotype.svg';
import logo from '../../assets/icons/ai-logo.svg';

const { Sider } = Layout;

const rootSubmenuKeys = [
  'data-editor',
  'user-editor',
  'team-editor',
  'import-export',
  'account',
  'aid-organization',
];

interface SideNavProps {
  isNexisAdmin: boolean;
  collapsed: boolean;
}

const SideNav = ({ isNexisAdmin, collapsed }: SideNavProps) => {
  const [openKeysOverride, setOpenKeysOverride] = useState<string[] | null>(
    null,
  );

  const location = useLocation();
  type MenuItem = Required<MenuProps>['items'][number];

  const allMenuItems = [
    {
      key: 'data-editor',
      icon: <ProfileOutlined />,
      title: 'Data Editor',
      hidden: isNexisAdmin,
      children: [
        {
          key: 'vault',
          path: '/data-editor/vault',
          icon: <SolutionOutlined />,
          title: 'People Editor',
        },
        {
          key: 'org-events',
          path: '/data-editor/org-events',
          icon: <ProjectOutlined />,
          title: 'Company Events Editor',
        },
        {
          key: 'insider-events',
          path: '/data-editor/insider-events',
          icon: <DatabaseOutlined />,
          title: 'Insider Events Editor',
        },
        {
          key: 'company',
          path: '/data-editor/company',
          icon: <BankOutlined />,
          title: 'Company Editor',
        },
        {
          key: 'company-merge',
          path: '/data-editor/company-merge',
          icon: <MergeCellsOutlined />,
          title: 'Company Merge',
        },
        {
          key: 'person-merge',
          path: '/data-editor/person-merge',
          icon: <MergeCellsOutlined />,
          title: 'Person Merge',
        },
        {
          key: 'profile-edits',
          path: '/data-editor/profile-edits',
          icon: <EditOutlined />,
          title: 'Profile Edits',
        },
      ],
    },
    {
      key: 'user-editor',
      icon: <UserAddOutlined />,
      title: 'Users Editor',
      children: isNexisAdmin
        ? [
            {
              key: 'users',
              path: '/user-editor/users',
              icon: <SolutionOutlined />,
              title: 'Users',
            },
            {
              key: 'credits-log',
              path: '/user-editor/credits-log',
              icon: <DollarOutlined />,
              title: 'Credits Log',
            },
          ]
        : [
            {
              key: 'users',
              path: '/user-editor/users',
              icon: <SolutionOutlined />,
              title: 'Users',
            },
            {
              key: 'users-mapping',
              path: '/user-editor/users-mapping',
              icon: <UserAddOutlined />,
              title: 'Users Mapping',
            },
            {
              key: 'credits-log',
              path: '/user-editor/credits-log',
              icon: <DollarOutlined />,
              title: 'Credits Log',
            },
            {
              key: 'user-setup-response',
              path: '/user-editor/user-setup-response',
              icon: <QuestionCircleOutlined />,
              title: 'User Setup Response',
            },
            {
              key: 'terms-acceptance',
              path: '/user-editor/terms-acceptance',
              icon: <AuditOutlined />,
              title: 'Terms Acceptance',
            },
          ],
    },
    {
      key: 'aid-organization',
      path: '/aid-organization',
      icon: <BankOutlined />,
      title: 'Aid Organization',
    },
    {
      key: 'aid-organization-subscription',
      path: '/aid-organization-subscription',
      icon: <DatabaseOutlined />,
      title: 'Aid Organization Subscription',
    },
    {
      key: 'comments',
      path: '/comments',
      icon: <CommentOutlined />,
      title: 'Comments',
      hidden: isNexisAdmin,
    },
    {
      key: 'api-usage',
      path: '/api-usage',
      icon: <ApiOutlined />,
      title: 'API Usage',
      hidden: isNexisAdmin,
    },
    {
      key: 'team-editor',
      path: '/team-editor',
      icon: <TeamOutlined />,
      title: 'Team Editor',
    },
    {
      key: 'import-export',
      icon: <FileOutlined />,
      title: 'Import/Export',
      children: [
        {
          key: 'contact-exports',
          path: '/import-export/contact-exports',
          icon: <ExportOutlined />,
          title: 'Contact Exports',
        },
        {
          key: 'contact-imports',
          path: '/import-export/contact-imports',
          icon: <ImportOutlined />,
          title: 'Contact Imports',
        },
        {
          key: 'crm-export-tracks',
          path: '/import-export/crm-export-tracks',
          hidden: isNexisAdmin,
          icon: <LinkOutlined />,
          title: 'Crm Export Tracks',
        },
      ],
    },
    {
      key: 'reports',
      path: '/reports',
      icon: <FormOutlined />,
      title: 'Reports',
    },
    {
      key: 'signed-refcodes',
      path: '/signed-refcodes',
      icon: <RadarChartOutlined />,
      title: 'Subscription Overrides',
      hidden: isNexisAdmin,
    },
    {
      key: 'referrercode',
      path: '/referrercode',
      icon: <AlignCenterOutlined />,
      title: 'Referrer Code (Legacy)',
    },
    {
      key: 'account',
      icon: <UserOutlined />,
      title: 'Account',
      hidden: isNexisAdmin,
      children: [
        {
          key: 'settings',
          path: '/account/settings/base',
          icon: <></>,
          title: 'Account Settings',
        },
      ],
    },
  ];

  const filteredMenuItems: MenuItem[] = allMenuItems.map((item) => {
    if (item.hidden) {
      return null;
    }
    if (item.children) {
      return {
        key: item.key,
        icon: item.icon,
        title: item.title,
        label: item.title,
        children: item.children.map((item) => {
          if (item.hidden) {
            return null;
          }
          return {
            key: item.key,
            icon: item.icon,
            title: item.title,
            label: <Link to={item.path}>{item.title}</Link>,
          };
        }),
      };
    } else {
      return {
        key: item.key,
        icon: item.icon,
        title: item.title,
        label: <Link to={item.path}>{item.title}</Link>,
      };
    }
  });

  const { openKeys: derivedOpenKeys, selectedKeys } = useMemo(() => {
    const parts = location.pathname.split('/');
    if (parts.length > 2) {
      return { openKeys: [parts[1]], selectedKeys: [parts[2]] };
    } else {
      return { openKeys: [] as string[], selectedKeys: [parts[1]] };
    }
  }, [location]);

  useEffect(() => {
    /** clicked an item when the menu was collapsed,
     * on expanding the view, show the selected item Submenu as expanded
     */
    if (!collapsed) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOpenKeysOverride(null);
    }
  }, [collapsed, location.pathname]);

  const openKeys = openKeysOverride ?? derivedOpenKeys;

  const onOpenChange = (keys) => {
    /** on a menu click  all the other menus gets collapsed  */
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      setOpenKeysOverride(keys);
    } else {
      setOpenKeysOverride(latestOpenKey ? [latestOpenKey] : []);
    }
  };

  return (
    <>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={256}
        theme="light"
        className={'sider'}
      >
        <div className="header">
          <Link to="/">
            <img className="logo" src={logo} alt="logo" />
            <img className="logotype" src={logotype} alt="logotype" />
          </Link>
        </div>
        <Menu
          mode="inline"
          openKeys={openKeys}
          selectedKeys={selectedKeys}
          onOpenChange={onOpenChange}
          items={filteredMenuItems}
        />
      </Sider>
      <div style={{ width: collapsed ? 80 : 256 }} />
      {/** Dummy div to adjust the spacing */}
    </>
  );
};

export default SideNav;
